/**
 * Product Main - Completely Rewritten
 * Handles variant selection, cart operations, wishlist, and gallery
 */

class CirqleProductPage {
  constructor() {
    this.product = JSON.parse(document.getElementById('product-json').textContent);
    this.variants = JSON.parse(document.getElementById('product-variants-json').textContent);
    this.selectedOptions = {};
    this.currentVariant = this.product.selected_or_first_available_variant;
    this.init();
  }

  init() {
    this.setupGallery();
    this.setupColorSwatches();
    this.setupSizeButtons();
    this.setupAddToCart();
    this.setupWishlist();
    this.loadWishlistState();
  }

  setupGallery() {
    const thumbs = document.querySelectorAll('.gallery-thumb');
    const mainImage = document.getElementById('main-product-image');
    if (!mainImage) return;
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const imageSrc = thumb.dataset.imageSrc;
        const imageSrcset = thumb.dataset.imageSrcset;
        mainImage.src = imageSrc;
        mainImage.srcset = imageSrcset;
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  }

  setupColorSwatches() {
    const swatches = document.querySelectorAll('.color-swatch');
    const selectedColorName = document.getElementById('selected-color-name');
    swatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        const value = swatch.dataset.value;
        const optionIndex = parseInt(swatch.dataset.optionIndex);
        this.selectedOptions[`option${optionIndex}`] = value;
        swatches.forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');
        if (selectedColorName) selectedColorName.textContent = value;
        this.updateVariant();
        this.updateSizeAvailability();
      });
    });
    if (swatches.length > 0) {
      const firstSwatch = swatches[0];
      this.selectedOptions[`option${parseInt(firstSwatch.dataset.optionIndex)}`] = firstSwatch.dataset.value;
    }
  }

  setupSizeButtons() {
    const sizeButtons = document.querySelectorAll('.size-btn');
    const selectedSizeName = document.getElementById('selected-size-name');
    sizeButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (button.classList.contains('unavailable')) return;
        const value = button.dataset.value;
        const optionIndex = parseInt(button.dataset.optionIndex);
        this.selectedOptions[`option${optionIndex}`] = value;
        sizeButtons.forEach(b => b.classList.remove('active'));
        button.classList.add('active');
        if (selectedSizeName) selectedSizeName.textContent = value;
        this.updateVariant();
      });
    });
    if (sizeButtons.length > 0) {
      const firstAvailable = Array.from(sizeButtons).find(btn => !btn.classList.contains('unavailable'));
      if (firstAvailable) {
        this.selectedOptions[`option${parseInt(firstAvailable.dataset.optionIndex)}`] = firstAvailable.dataset.value;
      }
    }
  }

  updateSizeAvailability() {
    const sizeButtons = document.querySelectorAll('.size-btn');
    sizeButtons.forEach(button => {
      const sizeValue = button.dataset.value;
      const optionIndex = parseInt(button.dataset.optionIndex);
      const testOptions = { ...this.selectedOptions };
      testOptions[`option${optionIndex}`] = sizeValue;
      const matchingVariant = this.variants.find(v => {
        return Object.keys(testOptions).every(key => v[key] === testOptions[key]);
      });
      if (matchingVariant && matchingVariant.available) {
        button.classList.remove('unavailable');
      } else {
        button.classList.add('unavailable');
      }
    });
  }

  updateVariant() {
    const variant = this.variants.find(v => {
      return Object.keys(this.selectedOptions).every(key => v[key] === this.selectedOptions[key]);
    });
    if (variant) {
      this.currentVariant = variant;
      this.updatePrice(variant);
      this.updateImage(variant);
      this.updateAddToCartButton(variant);
      this.updateStockCount(variant);
    }
  }

  updatePrice(variant) {
    const priceElement = document.getElementById('product-price');
    if (priceElement && variant.price) {
      priceElement.textContent = this.formatMoney(variant.price);
    }
  }

  updateImage(variant) {
    if (variant.featured_image && variant.featured_image.src) {
      const mainImage = document.getElementById('main-product-image');
      if (mainImage) {
        mainImage.src = variant.featured_image.src;
        mainImage.srcset = `${variant.featured_image.src} 1x`;
      }
    }
  }

  updateStockCount(variant) {
    const stockCount = document.getElementById('stock-count');
    if (stockCount && variant.inventory_quantity) {
      stockCount.textContent = variant.inventory_quantity;
    }
  }

  updateAddToCartButton(variant) {
    const addToCartBtn = document.getElementById('add-to-cart-btn');
    if (!addToCartBtn) return;
    addToCartBtn.dataset.variantId = variant.id;
    if (variant.available) {
      addToCartBtn.disabled = false;
      addToCartBtn.textContent = addToCartBtn.textContent.includes('SOLD OUT')
        ? 'ADD TO CART'
        : addToCartBtn.textContent;
    } else {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'SOLD OUT';
    }
  }

  setupAddToCart() {
    const addToCartBtn = document.getElementById('add-to-cart-btn');
    if (!addToCartBtn) return;

    addToCartBtn.addEventListener('click', async () => {
      const variantId = addToCartBtn.dataset.variantId;
      const originalText = addToCartBtn.textContent;

      addToCartBtn.textContent = 'ADDING...';
      addToCartBtn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        addToCartBtn.textContent = 'ADDED!';

        await this.updateCartCount();

        setTimeout(() => {
          this.openCartDrawer();
          addToCartBtn.textContent = originalText;
          addToCartBtn.disabled = false;
        }, 500);

      } catch (error) {
        console.error('Error adding to cart:', error);
        addToCartBtn.textContent = 'ERROR';
        setTimeout(() => {
          addToCartBtn.textContent = originalText;
          addToCartBtn.disabled = false;
        }, 2000);
      }
    });
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartBadge = document.querySelector('.cart-badge');
      if (cartBadge) cartBadge.textContent = `Cart · ${cart.item_count}`;
      const cartCount = document.querySelector('.cart-count');
      if (cartCount) cartCount.textContent = cart.item_count;
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  // ── FIXED: ab cirqleCollection ka openCartDrawer use hoga ──
  // quantity controls sahi aayenge
  openCartDrawer() {
    if (window.cirqleCollection) {
      window.cirqleCollection.openCartDrawer();
    }
  }

  setupWishlist() {
    const wishlistBtn = document.querySelector('.cta-wishlist');
    if (!wishlistBtn) return;
    wishlistBtn.addEventListener('click', () => {
      const productId = wishlistBtn.dataset.productId;
      const isActive = wishlistBtn.classList.toggle('active');
      this.saveWishlistState(productId, isActive);
      const heart = wishlistBtn.querySelector('.heart');
      if (heart) {
        heart.textContent = isActive ? '♥' : '♡';
        heart.style.color = isActive ? 'var(--lavender-dark)' : 'inherit';
      }
    });
  }

  saveWishlistState(productId, isWishlisted) {
    try {
      localStorage.setItem(`cirqle_wishlist_${productId}`, isWishlisted ? '1' : '0');
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  loadWishlistState() {
    try {
      const wishlistBtn = document.querySelector('.cta-wishlist');
      if (!wishlistBtn) return;
      const productId = wishlistBtn.dataset.productId;
      const isWishlisted = localStorage.getItem(`cirqle_wishlist_${productId}`) === '1';
      if (isWishlisted) {
        wishlistBtn.classList.add('active');
        const heart = wishlistBtn.querySelector('.heart');
        if (heart) {
          heart.textContent = '♥';
          heart.style.color = 'var(--lavender-dark)';
        }
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CirqleProductPage();
  });
} else {
  new CirqleProductPage();
}